import { useState, useEffect, useCallback } from 'react';
import { useEffectiveBusinessId } from './useEffectiveBusinessId';
import { fetchSupplierOptions, createSupplier } from '@/features/receipts/services/supplierService';

export interface SupplierOption {
  id: string;
  name: string;
}

interface UseSupplierOptionsReturn {
  suppliers: SupplierOption[];
  isLoading: boolean;
  error: string | null;
  createNewSupplier: (name: string) => Promise<string>;
  refetch: () => Promise<void>;
}

export function useSupplierOptions(): UseSupplierOptionsReturn {
  const effectiveBusinessId = useEffectiveBusinessId();
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSuppliers = useCallback(async () => {
    if (!effectiveBusinessId) {
      setSuppliers([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchSupplierOptions(effectiveBusinessId);
      setSuppliers(data);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load suppliers');
    } finally {
      setIsLoading(false);
    }
  }, [effectiveBusinessId]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const createNewSupplier = useCallback(
    async (name: string): Promise<string> => {
      if (!effectiveBusinessId) {
        throw new Error('No business selected');
      }

      const supplier = await createSupplier(effectiveBusinessId, name);

      // Add to local list
      setSuppliers((prev) => [...prev, { id: supplier.id, name: supplier.name }].sort((a, b) =>
        a.name.localeCompare(b.name)
      ));

      return supplier.id;
    },
    [effectiveBusinessId]
  );

  return {
    suppliers,
    isLoading,
    error,
    createNewSupplier,
    refetch: fetchSuppliers,
  };
}